import React, { useEffect, useState } from "react";

const ModalUI = ({
  open = true,
  heading = "Submit Test",
  subtitle = "Final review before you hand it in.",
  items = [],
  primaryButtonText = "Submit test",
  secondaryButtonText = "Cancel",
  onClose,
  onConfirm,
  showOverlay = true,
  isSubmitting = false,
  children,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!open) return null;

  const keyframes = `
    @keyframes mui_overlayFade {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes mui_cardRise {
      from { opacity: 0; transform: translateY(22px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0)     scale(1); }
    }
    @keyframes mui_haloPulse {
      0%, 100% { box-shadow: 0 0 0 0   rgba(99,102,241, 0.45); }
      50%      { box-shadow: 0 0 0 16px rgba(99,102,241, 0);    }
    }
    @keyframes mui_qIn {
      0%   { transform: scale(0.4) rotate(-12deg); opacity: 0; }
      70%  { transform: scale(1.15) rotate(0deg); }
      100% { transform: scale(1) rotate(0deg); opacity: 1; }
    }
    @keyframes mui_rowIn {
      from { opacity: 0; transform: translateX(-6px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes mui_glowMove {
      0%   { background-position: 0% 50%; }
      100% { background-position: 200% 50%; }
    }
    @keyframes mui_spin {
      to { transform: rotate(360deg); }
    }
  `;

  const overlay = (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        background:
          "radial-gradient(1200px 600px at 50% -10%, rgba(99,102,241,0.18), transparent 60%), rgba(8, 15, 22, 0.55)",
        backdropFilter: "blur(8px) saturate(140%)",
        WebkitBackdropFilter: "blur(8px) saturate(140%)",
        animation: "mui_overlayFade 0.25s ease-out",
        fontFamily:
          "'Inter', 'DM Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 460,
          background: "#ffffff",
          borderRadius: 24,
          overflow: "hidden",
          boxShadow:
            "0 30px 80px -10px rgba(2,6,23,0.45), 0 12px 24px -8px rgba(2,6,23,0.25), inset 0 1px 0 rgba(255,255,255,0.9)",
          animation: "mui_cardRise 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)",
          opacity: mounted ? 1 : 0,
        }}
      >
        {/* Animated gradient hero band */}
        <div
          style={{
            height: 96,
            background:
              "linear-gradient(120deg, #4338ca 0%, #6366f1 30%, #818cf8 55%, #6366f1 80%, #4338ca 100%)",
            backgroundSize: "200% 200%",
            animation: "mui_glowMove 6s linear infinite",
            position: "relative",
          }}
        >
          {/* Decorative dotted texture */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              opacity: 0.18,
              backgroundImage:
                "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)",
              backgroundSize: "14px 14px",
            }}
          />
          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              width: 32,
              height: 32,
              borderRadius: 999,
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.35)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backdropFilter: "blur(4px)",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.32)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.18)")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Floating circular badge (overlaps the band) */}
        <div
          style={{
            position: "absolute",
            top: 60,
            left: "50%",
            transform: "translateX(-50%)",
            width: 76,
            height: 76,
            borderRadius: "50%",
            background: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow:
              "0 10px 24px rgba(6, 95, 70, 0.28), inset 0 0 0 6px #ffffff",
            zIndex: 1,
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background:
                "linear-gradient(160deg, #6366f1 0%, #4338ca 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "mui_haloPulse 2.2s ease-out infinite",
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ animation: "mui_qIn 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.1s both" }}
            >
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "56px 28px 22px", textAlign: "center" }}>
          <h2
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            {heading}
          </h2>
          {subtitle && (
            <p
              style={{
                margin: "8px 0 0",
                fontSize: 14,
                color: "#64748b",
                lineHeight: 1.55,
                fontWeight: 500,
              }}
            >
              {subtitle}
            </p>
          )}

          {children ? children : items.length > 0 && (
            <ol
              style={{
                listStyle: "none",
                margin: "22px 0 0",
                padding: 0,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                textAlign: "left",
              }}
            >
              {items.map((item, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "12px 14px",
                    borderRadius: 14,
                    background: "#f8fafc",
                    border: "1px solid #eef2f7",
                    animation: `mui_rowIn 0.35s ease ${i * 70 + 200}ms both`,
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(160deg, #eef2ff 0%, #e0e7ff 100%)",
                      color: "#4338ca",
                      fontWeight: 800,
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "inset 0 0 0 1px rgba(67,56,202,0.18)",
                    }}
                  >
                    {i + 1}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: "#0f172a",
                        lineHeight: 1.35,
                      }}
                    >
                      {item.title}
                    </div>
                    {item.desc && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: 12.5,
                          color: "#64748b",
                          lineHeight: 1.5,
                        }}
                      >
                        {item.desc}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            gap: 10,
            padding: "16px 22px 22px",
            background:
              "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            borderTop: "1px solid #eef2f7",
          }}
        >
          {secondaryButtonText && (
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                flex: 1,
                padding: "12px 16px",
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                color: "#475569",
                background: "#ffffff",
                border: "1.5px solid #e2e8f0",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (isSubmitting) return;
                e.currentTarget.style.background = "#f1f5f9";
                e.currentTarget.style.borderColor = "#cbd5e1";
              }}
              onMouseLeave={(e) => {
                if (isSubmitting) return;
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              {secondaryButtonText}
            </button>
          )}
          {primaryButtonText && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={isSubmitting}
              style={{
                flex: 1.4,
                padding: "12px 18px",
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 700,
                color: "#fff",
                border: "none",
                cursor: isSubmitting ? "wait" : "pointer",
                background:
                  "linear-gradient(135deg, #6366f1 0%, #4338ca 100%)",
                boxShadow:
                  "0 8px 20px -6px rgba(67,56,202,0.55), inset 0 -2px 0 rgba(0,0,0,0.12)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                transition: "transform 0.15s ease, filter 0.15s ease",
                opacity: isSubmitting ? 0.85 : 1,
              }}
              onMouseEnter={(e) => {
                if (isSubmitting) return;
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.filter = "brightness(1.06)";
              }}
              onMouseLeave={(e) => {
                if (isSubmitting) return;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.filter = "none";
              }}
            >
              {isSubmitting ? (
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    border: "2px solid rgba(255,255,255,0.4)",
                    borderTopColor: "#fff",
                    animation: "mui_spin 0.8s linear infinite",
                    display: "inline-block",
                  }}
                />
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
              {primaryButtonText}
            </button>
          )}
        </div>
      </div>
      <style>{keyframes}</style>
    </div>
  );

  return showOverlay ? overlay : overlay;
};

export default ModalUI;
